import { onboardingapi } from './onboarding';

/**
 * Identity (step-up) verification contract served by the underwriting service
 * (fattunderwriting, SPX-2108) at `GET /team/verification`.
 *
 * The frontend keys off `questions.status` to decide what the "Verify Identity"
 * prompt renders on the review page:
 * - `null`     → step-up not required → prompt is hidden
 * - `pending`  → step-up required, IQ questions available → prompt + CTA
 * - `complete` → answers submitted → prompt shows the completed state
 */
export type TeamVerificationStatus = 'pending' | 'complete' | null;

/**
 * A single IDology IQ (step-up) question. Mirrors the underwriting service's
 * `IqQuestion` shape. Consumed by the IQ questions modal (SPX-2113); this
 * ticket only forwards the list.
 */
export interface IqQuestion {
  type: string;
  prompt: string;
  options: string[];
}

export interface IqQuestionsShape {
  status: TeamVerificationStatus;
  items: IqQuestion[];
}

export interface TeamVerificationData {
  questions: IqQuestionsShape;
}

/**
 * Fetch the merchant's identity (step-up) verification state. The route wraps
 * its payload in `{ data }`, so we unwrap to the `TeamVerificationData` here.
 */
export async function getTeamVerification(
  token: string,
): Promise<TeamVerificationData> {
  const response = await onboardingapi.get<{ data: TeamVerificationData }>(
    token,
    '/team/verification',
  );
  return response.data;
}

export interface VerificationAnswer {
  type: string;
  answer: string;
}

/**
 * Submit IQ answers to the underwriting service at `POST /team/verification`.
 * Returns 201 with no body — the backend is intentionally opaque about
 * pass/fail (SPX-2110). One-shot: subsequent calls silently no-op.
 */
export async function postTeamVerificationAnswers(
  token: string,
  answers: VerificationAnswer[],
): Promise<void> {
  await onboardingapi.post(token, '/team/verification', { answers });
}
